import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2, ArrowLeft, Calendar, Star } from 'lucide-react';
import { useFavorites } from '../favorites';
import { resolveImage } from '../api';

export default function Favorites() {
  const { items, remove, setDay, clear } = useFavorites();
  const [showByDay, setShowByDay] = useState(false);

  const grouped = useMemo(() => {
    if (!showByDay) return { 0: items };
    return items.reduce((acc, f) => {
      const d = f.day || 1;
      (acc[d] = acc[d] || []).push(f);
      return acc;
    }, {});
  }, [items, showByDay]);

  const dayKeys = showByDay ? Object.keys(grouped).sort((a,b) => Number(a) - Number(b)) : ['0'];

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-testid="favorites-empty">
        <Heart size={64} className="mx-auto mb-6 text-white/20"/>
        <h1 className="text-white text-3xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>I tuoi preferiti sono vuoti</h1>
        <p className="text-white/60 text-sm mb-8">Tocca il cuore su qualsiasi card per aggiungerla e costruire il tuo itinerario personale.</p>
        <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-black text-sm uppercase tracking-wider"
              style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
          Esplora YUP
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-14" data-testid="favorites-page">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-white text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>I miei preferiti</h1>
          <p className="text-white/60 text-sm mt-1">{items.length} attività salvate localmente sul tuo dispositivo</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowByDay(v => !v)}
                  data-testid="fav-toggle-itinerary"
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold ${showByDay ? 'bg-[color:var(--yup-primary)] text-black' : 'bg-white/5 text-white/70 border border-white/10'}`}>
            <Calendar size={14}/> Itinerario per giorno
          </button>
          <button onClick={() => { if (window.confirm('Svuotare tutti i preferiti?')) clear(); }}
                  className="text-red-400 text-xs px-2 py-2 hover:brightness-125">Svuota</button>
        </div>
      </div>

      {dayKeys.map(k => (
        <div key={k} className="mb-8">
          {showByDay && (
            <h2 className="text-xs uppercase tracking-[0.3em] text-[color:var(--yup-primary)] font-bold mb-3">
              Giorno {k}
            </h2>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {grouped[k].map(f => (
              <div key={f.id} className="group rounded-xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-[color:var(--yup-primary)]/40 transition"
                   data-testid={`fav-card-${f.id}`}>
                {f.image && (
                  <Link to={`/${f.city_slug}/cosa-vedere/${f.category_key}/${f.subcategory || ''}`.replace(/\/$/, '')}>
                    <img src={resolveImage(f.image)} alt={f.name} className="w-full aspect-[16/10] object-cover"/>
                  </Link>
                )}
                <div className="p-4">
                  <div className="flex items-start gap-2">
                    <h3 className="text-white font-bold flex-1 min-w-0">{f.name}</h3>
                    {typeof f.rating === 'number' && (
                      <div className="text-xs text-[color:var(--yup-primary)] flex items-center gap-0.5 flex-shrink-0">
                        <Star size={11} fill="currentColor"/> {f.rating.toFixed(1)}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-white/50 mt-1">{f.city_slug} · {f.category_key}</div>

                  {showByDay && (
                    <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                      {[1,2,3,4,5].map(d => (
                        <button key={d} onClick={() => setDay(f.id, d)}
                                className={`w-7 h-7 rounded-full text-xs font-bold ${f.day === d ? 'bg-[color:var(--yup-primary)] text-black' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}>
                          {d}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex justify-between items-center">
                    <Link to={`/${f.city_slug}/cosa-vedere/${f.category_key}/${f.subcategory || ''}`.replace(/\/$/, '')}
                          className="text-[color:var(--yup-primary)] text-xs uppercase tracking-widest font-semibold hover:underline">
                      Apri →
                    </Link>
                    <button onClick={() => remove(f.id)} data-testid={`fav-remove-${f.id}`} className="text-red-400/70 hover:text-red-400 p-1">
                      <Trash2 size={15}/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Link to="/" className="inline-flex items-center gap-2 mt-6 text-[color:var(--yup-primary)] text-sm">
        <ArrowLeft size={14}/> Torna alla home
      </Link>
    </div>
  );
}
